import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { FormCategory, ProcedureType } from '@/types/cgt'

// ─── Center Setup (z8nrz7f0vt backend / z8nrz7f0vu UI) ────────────────────────
// The lists a center defines once and every resource profile points at: shift
// definitions, the shift pattern library, and the support activity (SA) and
// unavailability (UA) pick lists. Reads need resource_center.read (or
// resource_capabilities.read); every write needs resource_center.manage.

export interface ShiftDefinition {
  id: string
  code: string
  name: string
  /** Wall-clock "HH:mm" in the center's own day. */
  startTime: string
  endTime: string
  breakMinutes: number
  /** Start to end, break included. */
  spanHours: number
  /** Net hours (span less break) — what capacity is built from. Derived by the server. */
  hours: number
  /** Weekdays it may be worked on, 0 = Sunday … 6 = Saturday, ascending. */
  weekdays: number[]
  wrapsMidnight: boolean
  active: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface PatternShiftRef {
  id: string
  code: string
  name: string
  hours: number
  active: boolean
}

export interface ShiftPatternDay {
  /** 0 = the Sunday of the week a resource starts the pattern. */
  dayIndex: number
  isOn: boolean
  /** The shift worked that day; null on an OFF day. */
  shift: PatternShiftRef | null
}

export interface ShiftPattern {
  id: string
  name: string
  description: string | null
  cycleDays: number
  /** Every shift the cycle uses, in the order it first appears. */
  shifts: PatternShiftRef[]
  days: ShiftPatternDay[]
  workingDays: number
  averageWeeklyHours: number
  active: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface SupportActivityType {
  id: string
  code: string
  name: string
  defaultHoursPerDay: number
  active: boolean
}

export interface UnavailabilityType {
  id: string
  code: string
  name: string
  /** May take a number of hours out of a shift, not only whole days. */
  allowPartial: boolean
  active: boolean
}

export interface ShiftDefinitionPayload {
  code: string
  name: string
  startTime: string
  endTime: string
  breakMinutes: number
  weekdays: number[]
}

export interface PatternDayPayload {
  isOn: boolean
  /** Required on an ON day. */
  shiftId?: string | null
}

export interface ShiftPatternPayload {
  name: string
  description?: string | null
  /** One entry per cycle day; the cycle length is the number of entries. */
  days: PatternDayPayload[]
}

export interface SupportActivityTypePayload {
  code: string
  name: string
  defaultHoursPerDay: number
}

export interface UnavailabilityTypePayload {
  code: string
  name: string
  allowPartial: boolean
}

export interface LoadDefaultsResult {
  shifts: number
  patterns: number
  supportActivityTypes: number
  unavailabilityTypes: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const send = <T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown) =>
  request<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) })

export const resourceCenterApi = {
  listShifts: () => request<ShiftDefinition[]>('/resource-center/shifts'),
  createShift: (body: ShiftDefinitionPayload) =>
    send<ShiftDefinition>('POST', '/resource-center/shifts', body),
  updateShift: (id: string, body: Partial<ShiftDefinitionPayload> & { active?: boolean }) =>
    send<ShiftDefinition>('PATCH', `/resource-center/shifts/${id}`, body),
  // 409 while a pattern runs on it — deactivate instead.
  removeShift: (id: string) => send<{ ok: true }>('DELETE', `/resource-center/shifts/${id}`),

  listPatterns: () => request<ShiftPattern[]>('/resource-center/patterns'),
  createPattern: (body: ShiftPatternPayload) =>
    send<ShiftPattern>('POST', '/resource-center/patterns', body),
  updatePattern: (id: string, body: Partial<ShiftPatternPayload> & { active?: boolean }) =>
    send<ShiftPattern>('PATCH', `/resource-center/patterns/${id}`, body),
  removePattern: (id: string) => send<{ ok: true }>('DELETE', `/resource-center/patterns/${id}`),

  listSupportActivityTypes: () =>
    request<SupportActivityType[]>('/resource-center/support-activity-types'),
  createSupportActivityType: (body: SupportActivityTypePayload) =>
    send<SupportActivityType>('POST', '/resource-center/support-activity-types', body),
  updateSupportActivityType: (
    id: string,
    body: Partial<SupportActivityTypePayload> & { active?: boolean },
  ) => send<SupportActivityType>('PATCH', `/resource-center/support-activity-types/${id}`, body),
  removeSupportActivityType: (id: string) =>
    send<{ ok: true }>('DELETE', `/resource-center/support-activity-types/${id}`),

  listUnavailabilityTypes: () =>
    request<UnavailabilityType[]>('/resource-center/unavailability-types'),
  createUnavailabilityType: (body: UnavailabilityTypePayload) =>
    send<UnavailabilityType>('POST', '/resource-center/unavailability-types', body),
  updateUnavailabilityType: (
    id: string,
    body: Partial<UnavailabilityTypePayload> & { active?: boolean },
  ) => send<UnavailabilityType>('PATCH', `/resource-center/unavailability-types/${id}`, body),
  removeUnavailabilityType: (id: string) =>
    send<{ ok: true }>('DELETE', `/resource-center/unavailability-types/${id}`),

  /** Adds the standard lists the center does not have yet; never overwrites. */
  loadDefaults: () => send<LoadDefaultsResult>('POST', '/resource-center/defaults'),
}

// ─── Assignment groups & resource profiles (z8nrz7f0vv backend / z8nrz7f0vw UI)
// A group is a named team (Micro Team, Chemistry-2 Team…) holding the forms its
// members work on. A person belongs to one or more groups: one Primary — shown
// as their Team — and the rest Secondary. Reads need resource_capabilities.read
// (or resource_center.read); writes need resource_capabilities.manage.

export interface AssignmentGroupForm {
  familyId: string
  /** Null when the form no longer has an active version — kept so the gap shows. */
  name: string | null
  category: FormCategory | null
}

export interface AssignmentGroupMember {
  userId: string
  name: string
  isPrimary: boolean
}

export interface AssignmentGroup {
  id: string
  name: string
  description: string | null
  forms: AssignmentGroupForm[]
  members: AssignmentGroupMember[]
  /** Teams the group is part of (z8nrz7f7dk). A group may be in several. */
  teams: { id: string; name: string }[]
  createdBy: string
}

/** A team (z8nrz7f7dk): a level above groups. People are in its groups, never in it directly. */
export interface Team {
  id: string
  name: string
  description: string | null
  groups: { id: string; name: string }[]
  /** Distinct current members of any of its groups. */
  memberCount: number
  createdBy: string
}

export interface TeamPayload {
  name: string
  description?: string | null
}

export interface GroupFormOption {
  familyId: string
  name: string
  category: FormCategory
  /** The procedures whose sequence includes this form — for the Procedure filter. */
  procedureFamilyIds: string[]
}

export interface GroupProcedureOption {
  familyId: string
  name: string
  type: ProcedureType
}

export interface GroupFormOptions {
  forms: GroupFormOption[]
  procedures: GroupProcedureOption[]
}

export interface AssignmentGroupPayload {
  name: string
  description?: string | null
}

/** PROCESS, REVIEW, QA, SUPPORT — the PR/RV/QA/SA chips. */
export type ResourceWorkType = 'PROCESS' | 'REVIEW' | 'QA' | 'SUPPORT'

export interface WorkSplit {
  id: string
  effectiveFrom: string
  isDynamic: boolean
  processPct: number
  reviewPct: number
  qaPct: number
  supportPct: number
  /** Fixed split: its non-zero percentages. Dynamic: chosen. */
  workTypes: ResourceWorkType[]
}

export interface ResourceShiftAssignment {
  id: string
  type: 'FIXED' | 'PATTERN' | 'VARIABLE'
  effectiveFrom: string
  effectiveTo: string | null
  shift: {
    id: string
    code: string
    name: string
    startTime: string
    endTime: string
    hours: number
  } | null
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: number[]
  pattern: { id: string; name: string; cycleDays: number } | null
}

/** One row of the Resources grid: a member as they are set up today. */
export interface ResourceSummary {
  userId: string
  name: string
  email: string
  enabled: boolean
  isSupervisor: boolean
  supervisor: { id: string; name: string } | null
  /** Every team any of their groups is in (a group may be in several). */
  teams: { id: string; name: string }[]
  /** Their groups, Primary first. */
  groups: { id: string; name: string; isPrimary: boolean }[]
  split: WorkSplit | null
  shift: ResourceShiftAssignment | null
}

export interface ProfileGroupPayload {
  groupId: string
  isPrimary: boolean
}

const put = <T>(path: string, body: unknown) => send<T>('PUT', path, body)

export const resourceProfilesApi = {
  listGroups: () => request<AssignmentGroup[]>('/assignment-groups'),
  groupFormOptions: () => request<GroupFormOptions>('/assignment-groups/form-options'),
  createGroup: (body: AssignmentGroupPayload & { formFamilyIds?: string[] }) =>
    send<AssignmentGroup>('POST', '/assignment-groups', body),
  updateGroup: (id: string, body: Partial<AssignmentGroupPayload>) =>
    send<AssignmentGroup>('PATCH', `/assignment-groups/${id}`, body),
  /** Replaces the group's whole form list. */
  setGroupForms: (id: string, formFamilyIds: string[]) =>
    put<AssignmentGroup>(`/assignment-groups/${id}/forms`, { formFamilyIds }),
  /** Refused (409) while anyone is still in the group. */
  deleteGroup: (id: string) => send<{ ok: true }>('DELETE', `/assignment-groups/${id}`),

  listTeams: () => request<Team[]>('/teams'),
  createTeam: (body: TeamPayload & { groupIds?: string[] }) => send<Team>('POST', '/teams', body),
  updateTeam: (id: string, body: Partial<TeamPayload>) => send<Team>('PATCH', `/teams/${id}`, body),
  /** Replaces the team's whole group list. */
  setTeamGroups: (id: string, groupIds: string[]) => put<Team>(`/teams/${id}/groups`, { groupIds }),
  /** Its groups and their people stay as they are. */
  deleteTeam: (id: string) => send<{ ok: true }>('DELETE', `/teams/${id}`),

  listResources: () => request<ResourceSummary[]>('/resource-profiles'),
  getProfile: (userId: string) => request<ResourceProfile>(`/resource-profiles/${userId}`),
  /** Replaces the person's whole group list; at most one Primary. */
  setGroups: (userId: string, groups: ProfileGroupPayload[]) =>
    put<ResourceProfile>(`/resource-profiles/${userId}/groups`, { groups }),
  /** Who they report to and their lab role. Both are global, not per center. */
  setSupervision: (userId: string, body: { supervisorId?: string | null; isSupervisor?: boolean }) =>
    put<ResourceProfile>(`/resource-profiles/${userId}/supervision`, body),

  /** A split from a date on; the same date again replaces that row. */
  setSplit: (userId: string, body: WorkSplitPayload) =>
    put<ResourceProfile>(`/resource-profiles/${userId}/split`, body),
  removeSplit: (userId: string, id: string) =>
    send<ResourceProfile>('DELETE', `/resource-profiles/${userId}/split/${id}`),

  /** Starts a shift assignment; the running one closes the day before. */
  addShiftAssignment: (userId: string, body: ShiftAssignmentPayload) =>
    send<ResourceProfile>('POST', `/resource-profiles/${userId}/shift-assignments`, body),
  /** Only the latest can be removed; the one before runs on. */
  removeShiftAssignment: (userId: string, id: string) =>
    send<ResourceProfile>('DELETE', `/resource-profiles/${userId}/shift-assignments/${id}`),
  /** A Variable shift's days in a window. */
  listShiftDays: (userId: string, from: string, to: string) =>
    request<ShiftDayView[]>(`/resource-profiles/${userId}/shift-days?from=${from}&to=${to}`),
  setShiftDays: (userId: string, days: { date: string; shiftId: string | null }[]) =>
    put<ResourceProfile>(`/resource-profiles/${userId}/shift-days`, { days }),

  addSupportActivity: (userId: string, body: SupportActivityPayload) =>
    send<ResourceProfile>('POST', `/resource-profiles/${userId}/support-activities`, body),
  updateSupportActivity: (userId: string, id: string, body: Partial<Omit<SupportActivityPayload, 'supportActivityTypeId'>>) =>
    send<ResourceProfile>('PATCH', `/resource-profiles/${userId}/support-activities/${id}`, body),
  removeSupportActivity: (userId: string, id: string) =>
    send<ResourceProfile>('DELETE', `/resource-profiles/${userId}/support-activities/${id}`),

  addUnavailability: (userId: string, body: UnavailabilityPayload) =>
    send<ResourceProfile>('POST', `/resource-profiles/${userId}/unavailability`, body),
  /** APPROVED only by the person's supervisor (or a superadmin) — 403 otherwise. */
  updateUnavailability: (userId: string, id: string, body: Partial<Omit<UnavailabilityPayload, 'unavailabilityTypeId'>>) =>
    send<ResourceProfile>('PATCH', `/resource-profiles/${userId}/unavailability/${id}`, body),
  removeUnavailability: (userId: string, id: string) =>
    send<ResourceProfile>('DELETE', `/resource-profiles/${userId}/unavailability/${id}`),

  /** Daily capacity for the center (or one person), at most 62 days. */
  capacity: (from: string, to: string, userId?: string) =>
    request<ResourceCapacity[]>(
      `/resource-capacity?from=${from}&to=${to}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`,
    ),
}

// ─── Profile detail, schedule & capacity (z8nrz7f0vw / z8nrz7f0vy) ───────────

export interface SupportActivityView {
  id: string
  type: { id: string; code: string; name: string }
  hoursPerDay: number
  fromDate: string
  toDate: string | null
}

export interface UnavailabilityView {
  id: string
  type: { id: string; code: string; name: string; allowPartial: boolean }
  fromDate: string
  toDate: string
  /** Null = the whole day. */
  hours: number | null
  status: 'PLANNED' | 'APPROVED'
  note: string | null
}

/** Everything on one person's profile, history included. */
export interface ResourceProfile extends ResourceSummary {
  splits: WorkSplit[]
  shiftAssignments: ResourceShiftAssignment[]
  supportActivities: SupportActivityView[]
  unavailabilities: UnavailabilityView[]
}

export interface WorkSplitPayload {
  effectiveFrom: string
  isDynamic?: boolean
  processPct?: number
  reviewPct?: number
  qaPct?: number
  supportPct?: number
  /** Dynamic only. */
  workTypes?: ResourceWorkType[]
}

export interface ShiftAssignmentPayload {
  type: 'FIXED' | 'PATTERN' | 'VARIABLE'
  effectiveFrom: string
  shiftId?: string
  weekdays?: number[]
  patternId?: string
}

export interface ShiftDayView {
  date: string
  shiftId: string | null
  shiftCode: string | null
}

export interface SupportActivityPayload {
  supportActivityTypeId: string
  /** Defaults to the type's hours per day. */
  hoursPerDay?: number
  fromDate: string
  toDate?: string | null
}

export interface UnavailabilityPayload {
  unavailabilityTypeId: string
  fromDate: string
  toDate: string
  /** Null or absent = whole days. */
  hours?: number | null
  status?: 'PLANNED' | 'APPROVED'
  note?: string | null
}

/**
 * - ON — working; the hours apply.
 * - OFF — no shift that day.
 * - NOT_STARTED — before their first shift assignment (the "—" cell).
 * - UNAVAILABLE — a working day wholly taken by an absence.
 */
export type CapacityDayState = 'ON' | 'OFF' | 'NOT_STARTED' | 'UNAVAILABLE'

export interface CapacityDay {
  date: string
  state: CapacityDayState
  shiftCode: string | null
  uaCode: string | null
  shiftHrs: number
  uaHrs: number
  saHrs: number
  prHrs: number
  rvHrs: number
  qaHrs: number
  /** Dynamic (or no split): one open pool for the scheduler. */
  flexHrs: number
  /** The work the flex hours may go to; empty when there are none. */
  flexTypes: ResourceWorkType[]
}

export type CapacityTotals = Omit<CapacityDay, 'date' | 'state' | 'shiftCode' | 'uaCode' | 'flexTypes'>

export interface ResourceCapacity {
  userId: string
  name: string
  enabled: boolean
  days: CapacityDay[]
  totals: CapacityTotals
}
